import { Baggage, BaggageType, OnlyAviaFare, TourPackage } from '@appTypes/api.types';
import { FlightPriceProviderType } from '@appTypes/common.types';
import { aviaFareId, aviaFareKey, isBundleList, isUnnamedFare } from '@pages/avia-page/avia-fares';

/**
 * Tour fare cards - pure rules of the website side (docs features/flight-fare-picker/qa.md "Tours - fare cards and bundles (B2)": R-TB3 the row's
 * "N fares", R-TB4 the Details open on the row's fare, R-TB9 Other flights = other itineraries, R-TB10 another flight or cabin clears the fare,
 * R-TB12 the cart keeps the fare). The cards come from packages-api (`package.fares`), cheapest first; this file only reads them.
 */

const toKebab = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .split(/[^a-z0-9]/)
    .filter(Boolean)
    .join('-');

/**
 * `{f}` of the tour test ids: the flight numbers of the first segment of each leg, lower case (`mu5066-mu5065`) - derived from the data, never an index,
 * so a card keeps its id when the cards are re-sorted or the tour is re-searched.
 */
export const tourFlightKey = (pkg: Pick<TourPackage, 'flight'> | null | undefined): string =>
  [pkg?.flight?.departureSegments?.[0]?.flightCode, pkg?.flight?.returnSegments?.[0]?.flightCode]
    .map((code) => toKebab(code ?? ''))
    .filter(Boolean)
    .join('-');

/** `HHmm` of a segment's departure, read as sent (the API's local time string; a Date in tests) - never converted to the browser's zone. */
const departureHhmm = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  if (typeof date === 'string') {
    const time = /T(\d{2}):(\d{2})/.exec(date);
    return time ? `${time[1]}${time[2]}` : '';
  }
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}`;
};

/**
 * The checked baggage included in one leg's fare (first passenger that states it): `nobag`, `20kg`, `2x23kg`, `1pc` (a piece without a weight);
 * '' when the leg states no included baggage at all. Paid options (`included` false) and cabin bags are not counted.
 */
const includedBagToken = (baggages: Baggage[] | null | undefined): string => {
  const included = (baggages ?? []).filter((b) => b?.included);
  if (!included.length) return '';
  const checked = included.flatMap((b) => b.bagDetails ?? []).filter((d) => d?.bagType === BaggageType.Checked);
  if (!checked.length) return 'nobag';
  return checked
    .map((d) => {
      const count = d.count && d.count > 1 ? d.count : 1;
      if (!d.weight || d.weight <= 0) return `${count}pc`;
      const unit = toKebab(d.weightUnit ?? '') || 'kg';
      const pieces = count > 1 ? count + 'x' : '';
      return pieces + d.weight + unit;
    })
    .join('-');
};

/** `{bag}` of the Other flights row id: the included checked baggage of the outbound and the return leg, one token when both are the same. */
const tourIncludedBaggageKey = (flight: TourPackage['flight'] | null | undefined): string => {
  const passenger = (flight?.passengers ?? []).find(
    (p) => Boolean(p?.forwardBaggages?.length) || Boolean(p?.backwardBaggages?.length)
  );
  const out = includedBagToken(passenger?.forwardBaggages);
  const back = includedBagToken(passenger?.backwardBaggages);
  if (!out && !back) return '';
  if (!flight?.returnSegments?.length) return out || 'nobag';
  const outbound = out || 'nobag';
  const inbound = back || 'nobag';
  return outbound === inbound ? outbound : `${outbound}-${inbound}`;
};

/**
 * `{o}` of the Other flights row test ids (docs features/flight-fare-picker/qa.md R-TB9 / test-id legend, docs#448): EVERY segment, outbound then return,
 * as flight number + departure `HHmm`, then the provider (`myagent`, `flydubai`, `airarabia`, `blocks` ...), the cabin when it is not Economy, the
 * fare family and the included checked baggage (`nobag`, `20kg`, `10kg-20kg` when the legs differ) - derived from the data, never an index, so two
 * itineraries that share their first flights (QR340 with four connections) and two fares of one flight that differ only in baggage (My Agent DP991
 * Basic / Value) keep different ids: `qr340-1615-qr1054-0350-qr1055-0225-qr235-0805-myagent`, `fz968-0640-fz967-0010-flydubai-business-flex`,
 * `dp991-0700-ut716-1625-myagent-nobag` / `dp991-0700-ut716-1625-myagent-10kg-20kg`.
 */
export const tourOtherFlightKey = (pkg: Pick<TourPackage, 'flight'> | null | undefined): string => {
  const flight = pkg?.flight;
  const segments = [...(flight?.departureSegments ?? []), ...(flight?.returnSegments ?? [])];
  const legs = segments.flatMap((s) => [
    toKebab(s?.flightCode ?? ''),
    departureHhmm(s?.departureFlightDate as Date | string | undefined)
  ]);
  const provider = flight?.providerType
    ? (FlightPriceProviderType[flight.providerType] ?? `provider-${flight.providerType}`)
    : '';
  const cabin = toKebab(flight?.fare?.cabin ?? segments[0]?.class ?? flight?.class ?? '');
  const fare = flight?.fare?.code ?? flight?.fare?.name ?? '';
  return [...legs, toKebab(provider), cabin === 'economy' ? '' : cabin, toKebab(fare), tourIncludedBaggageKey(flight)]
    .filter(Boolean)
    .join('-');
};

/** The cards of a package, cheapest first; [] when packages-api sent none (switch off, a block, a one-fare flight). */
export const tourFares = (pkg: Pick<TourPackage, 'fares'> | null | undefined): OnlyAviaFare[] =>
  [...(pkg?.fares ?? [])].sort((a, b) => (a.totalPrice ?? 0) - (b.totalPrice ?? 0));

/** R-TB2 / R-TB4: the picker is offered only when the flight has more than one fare; otherwise the Details stay as they are today. */
export const hasTourFarePicker = (pkg: Pick<TourPackage, 'fares'> | null | undefined): boolean =>
  (pkg?.fares?.length ?? 0) > 1;

/**
 * The card the package is priced at (R-TB4 / R-TB12):
 *   - a Fly Dubai brand / My Agent family is its own package id - the ONE card on the package's id is it;
 *   - cards sharing the package's id (Air Arabia bundles, My Agent "on open" families) are told apart by `selectedFareCode` (the fare priced into the
 *     package; null / empty = the fare without a bundle, the card with no code and no name);
 *   - nothing matches: the first card on the package's id, else the cheapest.
 */
export const selectedTourFare = (
  pkg: Pick<TourPackage, 'fares' | 'id' | 'selectedFareCode'> | null | undefined
): OnlyAviaFare | null => {
  const fares = tourFares(pkg);
  if (!fares.length) return null;
  const own = fares.filter((f) => f.packageId === pkg?.id);
  if (own.length === 1) return own[0];
  const candidates = own.length ? own : fares;
  const code = pkg?.selectedFareCode ?? null;
  const byCode = candidates.find((f) => (code ? f.code === code || f.name === code : isUnnamedFare(f)));
  return byCode ?? candidates[0];
};

/** The code the checkout sends for a card: the bundle / family name; null for Air Arabia's fare without a bundle (R-TB6, R-TB12). */
export const tourFareCode = (fare: OnlyAviaFare | null | undefined): string | null => fare?.code ?? fare?.name ?? null;

/** The id of the selected card, as the picker identifies cards. */
export const selectedTourFareId = (pkg: Parameters<typeof selectedTourFare>[0]): string =>
  aviaFareId(selectedTourFare(pkg));

/** `{k}` of a card's test ids: `mu5066-mu5065-economy-flex`; the picker builds it from `tourFlightKey` + `aviaFareKey`. */
export const tourFareTestKey = (pkg: Pick<TourPackage, 'flight'> | null | undefined, fare: OnlyAviaFare): string =>
  [tourFlightKey(pkg), aviaFareKey(fare)].filter(Boolean).join('-');

/**
 * R-TB12: what the cart line says - "Bundle: Ultimate" for an Air Arabia bundle list, "Fare: VALUE" for a brand / family; null for a flight
 * without a chosen fare (nothing to say) or the Air Arabia fare without a bundle.
 */
export const cartFareLine = (
  fares: readonly OnlyAviaFare[] | null | undefined,
  selectedFareCode: string | null | undefined
): { kind: 'bundle' | 'fare'; name: string } | null => {
  if (!selectedFareCode) return null;
  const bundles = isBundleList([...(fares ?? [])]);
  return { kind: bundles ? 'bundle' : 'fare', name: selectedFareCode };
};
